import React, { useState } from 'react';
import { Plus, Tag, Eye, EyeOff, Edit, Trash2, X, Sparkles, Image as ImageIcon, ListPlus, Minus, CheckCircle2, Package, Layers, Loader2 } from 'lucide-react';
import { Service, ServiceItem, Language } from '../types';
import { SERVICE_CATEGORIES, formatRiel } from '../constants';
import { generateServiceImage } from '../services/geminiService';
import { useTranslation } from '../LanguageContext';

interface ShopServicesProps {
  services: Service[];
  isOwner: boolean;
  shopId: string;
  onAddService: (newService: Service) => void;
  onUpdateService: (updatedService: Service) => void;
  onRemoveService: (serviceId: string) => void;
}

export const ShopServices: React.FC<ShopServicesProps> = ({ 
  services, 
  isOwner, 
  shopId,
  onAddService, 
  onUpdateService, 
  onRemoveService 
}) => {
  const { language } = useTranslation();
  const [isAddingService, setIsAddingService] = useState(false);
  const [editingServiceId, setEditingServiceId] = useState<string | null>(null);
  
  const [newServiceName, setNewServiceName] = useState('');
  const [newServiceCategory, setNewServiceCategory] = useState(SERVICE_CATEGORIES[0]);
  const [newServiceDescription, setNewServiceDescription] = useState('');
  const [newServiceImage, setNewServiceImage] = useState<string | null>(null);
  const [newServiceStatus, setNewServiceStatus] = useState<'PUBLIC' | 'PRIVATE'>('PUBLIC');
  const [isGeneratingImage, setIsGeneratingImage] = useState(false);
  
  const [tempServiceItems, setTempServiceItems] = useState<ServiceItem[]>([]);
  const [currentItemName, setCurrentItemName] = useState('');
  const [currentItemPrice, setCurrentItemPrice] = useState('');

  const texts: Record<string, Record<Language, string>> = {
    title: { EN: 'Service Catalog', KH: 'កាតាឡុកសេវាកម្ម', ZH: '服务目录', JA: 'サービスカタログ', KO: '서비스 카탈로그' },
    sub: { EN: 'Define your repair packages. Professional pricing increases trust.', KH: 'កំណត់កញ្ចប់ជួសជុលរបស់អ្នក។ តម្លៃច្បាស់លាស់បង្កើនទំនុកចិត្ត។', ZH: '定义您的维修包。专业的定价可增加信任。', JA: '修理パッケージを定義しましょう。プロの価格設定が信頼を高めます។', KO: '수리 패키지를 정의하십시오. 전문적인 가격 책정은 신뢰를 높입니다.' },
    add_btn: { EN: 'Add New Service', KH: 'បន្ថែមសេវាកម្មថ្មី', ZH: '添加新服务', JA: '新サービス追加', KO: '새 서비스 추가' },
    edit: { EN: 'Edit Package', KH: 'កែសម្រួលកញ្ចប់', ZH: '编辑包裹', JA: 'パッケージ編集', KO: '패キ지 편집' },
    create: { EN: 'Create Package', KH: 'បង្កើតកញ្ចប់', ZH: '创建包裹', JA: 'パッケージ作成', KO: '패키지 생성' },
    manage_pricing: { EN: 'Manage pricing and details.', KH: 'គ្រប់គ្រងតម្លៃ និងព័ត៌មានលម្អិត។', ZH: '管理定价和详情。', JA: '価格と詳細を管理します。', KO: '가격 및 세부 정보를 관리하십시오.' },
    svc_name: { EN: 'Service Name', KH: 'ឈ្មោះសេវាកម្ម', ZH: '服务名称', JA: 'サービス名', KO: '服务 이름' },
    cat: { EN: 'Category', KH: 'ប្រភេទ', ZH: '类别', JA: 'カテゴリ', KO: '카테고리' },
    visibility: { EN: 'Visibility Status', KH: 'ស្ថានភាពមើលឃើញ', ZH: '可见性状态', JA: '表示状況', KO: '가시성 상태' },
    cover: { EN: 'Cover Image', KH: 'រូបភាពគម្រប', ZH: '封面图片', JA: 'カバー画像', KO: '커버 이미지' },
    ai_gen: { EN: 'AI GENERATE', KH: 'បង្កើតដោយ AI', ZH: 'AI 生成', JA: 'AI 生成', KO: 'AI 생성' },
    upload_hint: { EN: 'Click to upload or use AI', KH: 'ចុចដើម្បីផ្ទុកឡើង ឬប្រើ AI', ZH: '点击上传或使用 AI', JA: 'クリックしてアップロード、或者 AI を使用', KO: '클릭하여 업로드하거나 AI 사용' },
    desc: { EN: 'Brief Description', KH: 'ការរៀបរាប់សង្ខេប', ZH: '简短描述', JA: '簡単な説明', KO: '간략한 설명' },
    line_items: { EN: 'Package Line Items', KH: 'ធាតុនៃកញ្ចប់សេវាកម្ម', ZH: '包裹项目', JA: 'パッケージ項目', KO: '패키지 항목' },
    item_placeholder: { EN: 'Item (e.g. Standard Fix)', KH: 'មុខទំនិញ (ឧ. ការជួសជុលស្តង់ដារ)', ZH: '项目（例如：标准修理）', JA: '項目（例：標準修理）', KO: '항목 (예: 표준 수리)' },
    amount_placeholder: { EN: 'Amount', KH: 'ចំនួនទឹកប្រាក់', ZH: '金额', JA: '金額', KO: '금액' },
    publish: { EN: 'Publish Package', KH: 'ដាក់ឱ្យប្រើប្រាស់', ZH: '发布包裹', JA: 'パッケージを公开', KO: '패키지 발행' },
    update: { EN: 'Update Package', KH: 'ធ្វើបច្ចុប្បន្នភាពកញ្ចប់', ZH: '更新包裹', JA: 'パッケージを更新', KO: '패키지 업데이트' },
  };

  const resetServiceForm = () => {
    setIsAddingService(false);
    setEditingServiceId(null);
    setNewServiceName('');
    setNewServiceCategory(SERVICE_CATEGORIES[0]);
    setNewServiceDescription('');
    setNewServiceImage(null);
    setTempServiceItems([]);
    setNewServiceStatus('PUBLIC');
    setCurrentItemName('');
    setCurrentItemPrice('');
  };

  const handleEditService = (service: Service) => {
    setEditingServiceId(service.id);
    setNewServiceName(service.name);
    setNewServiceCategory(service.category);
    setNewServiceDescription(service.description || '');
    setNewServiceImage(service.imageUrl || null);
    setNewServiceStatus(service.status);
    setTempServiceItems(service.items);
    setIsAddingService(true);
  };

  const handleGenerateServiceImage = async () => {
    if (!newServiceName) return;
    setIsGeneratingImage(true);
    const generatedImage = await generateServiceImage(newServiceName, newServiceDescription);
    if (generatedImage) {
      setNewServiceImage(generatedImage);
    }
    setIsGeneratingImage(false);
  };

  const handleAddItemToService = () => {
    if (!currentItemName || !currentItemPrice) return;
    
    const numericPrice = parseFloat(currentItemPrice.replace(/[^\d]/g, ''));
    if (isNaN(numericPrice)) return;

    const formattedPrice = formatRiel(numericPrice);
    const newItem: ServiceItem = {
      id: `si${Date.now()}`,
      name: currentItemName,
      price: formattedPrice
    };

    setTempServiceItems([...tempServiceItems, newItem]);
    setCurrentItemName('');
    setCurrentItemPrice('');
  };

  const handleRemoveTempItem = (id: string) => {
    setTempServiceItems(tempServiceItems.filter(i => i.id !== id));
  };

  const handleSaveServiceSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newServiceName || tempServiceItems.length === 0) return;

    const serviceData: Service = {
      id: editingServiceId || `sv${Date.now()}`,
      shopId: shopId,
      name: newServiceName,
      category: newServiceCategory,
      description: newServiceDescription,
      imageUrl: newServiceImage || undefined,
      items: tempServiceItems,
      status: newServiceStatus
    };

    if (editingServiceId) {
      onUpdateService(serviceData);
    } else {
      onAddService(serviceData);
    }

    resetServiceForm();
  };

  return (
    <div className="space-y-10 animate-in slide-in-from-bottom-4 duration-500">
      <div className="bg-slate-900 rounded-[3rem] p-10 text-white shadow-2xl shadow-slate-200 relative overflow-hidden group">
        <div className="absolute top-0 right-0 w-64 h-64 bg-violet-600/20 rounded-full blur-[80px] -mr-32 -mt-32 group-hover:bg-violet-600/30 transition-all duration-700"></div>
        <div className="relative z-10 flex flex-col md:flex-row justify-between items-center gap-8 text-center md:text-left">
          <div>
            <h2 className="text-3xl font-black mb-3 tracking-tighter">{texts.title[language]}</h2>
            <p className="text-slate-400 font-bold max-w-md text-lg">
              {texts.sub[language]}
            </p>
          </div>
          {isOwner && (
            <button 
              onClick={() => setIsAddingService(true)}
              className="bg-violet-600 text-white px-8 py-5 rounded-[2rem] font-black text-xs uppercase tracking-widest shadow-xl shadow-violet-500/30 flex items-center hover:bg-violet-50 transition-all transform active:scale-95"
            >
              <Plus size={20} className="mr-3" /> {texts.add_btn[language]}
            </button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10">
        {SERVICE_CATEGORIES.map(category => {
          const categoryServices = services.filter(s => s.category === category);
          if (categoryServices.length === 0) return null;

          return (
            <div key={category} className="bg-white rounded-[2.5rem] border border-slate-100 shadow-sm p-8 flex flex-col group/cat hover:shadow-xl transition-all duration-500">
              <div className="flex items-center justify-between mb-8">
                <h3 className="text-xs font-black text-slate-400 uppercase tracking-[0.2em] flex items-center">
                    <Layers size={14} className="mr-3 text-violet-500" /> {category}
                </h3>
                <span className="bg-slate-50 text-slate-400 px-3 py-1 rounded-full text-[10px] font-black">{categoryServices.length}</span>
              </div>

              <div className="space-y-8 flex-1">
                  {categoryServices.map(service => (
                    <div key={service.id} className="bg-slate-50/50 rounded-[2rem] overflow-hidden border border-slate-100 hover:bg-white hover:shadow-2xl hover:shadow-slate-200/50 transition-all duration-500 group">
                      {service.imageUrl && (
                        <div className="h-40 w-full overflow-hidden relative">
                          <img 
                            src={service.imageUrl} 
                            alt={service.name} 
                            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                          />
                          <div className="absolute top-4 left-4">
                            <span className={`px-3 py-1 rounded-lg text-[9px] font-black uppercase tracking-widest flex items-center backdrop-blur-md border border-white/20 ${service.status === 'PUBLIC' ? 'bg-emerald-500/90 text-white' : 'bg-slate-800/90 text-slate-300'}`}>
                              {service.status === 'PUBLIC' ? <Eye size={12} className="mr-2"/> : <EyeOff size={12} className="mr-2"/>}
                              {service.status}
                            </span>
                          </div>
                        </div>
                      )}
                      
                      <div className="p-6">
                        <div className="flex justify-between items-start mb-4">
                            <h4 className="font-black text-slate-900 text-xl tracking-tight leading-tight">{service.name}</h4>
                            <div className="flex space-x-1 opacity-0 group-hover:opacity-100 transition-opacity">
                              {isOwner && (
                                <>
                                  <button onClick={() => handleEditService(service)} className="w-8 h-8 rounded-lg bg-white border border-slate-100 flex items-center justify-center text-slate-400 hover:text-blue-500 transition-colors shadow-sm"><Edit size={14} /></button>
                                  <button onClick={() => onRemoveService(service.id)} className="w-8 h-8 rounded-lg bg-white border border-slate-100 flex items-center justify-center text-slate-400 hover:text-red-500 transition-colors shadow-sm"><Trash2 size={14} /></button>
                                </>
                              )}
                            </div>
                        </div>

                        <div className="space-y-2.5 mt-4 pt-4 border-t border-slate-200/50">
                          {service.items.map(item => (
                            <div key={item.id} className="flex justify-between items-center group/item">
                                <span className="text-xs font-bold text-slate-500 flex items-center">
                                  <CheckCircle2 size={12} className="mr-2 text-violet-400 opacity-0 group-hover/item:opacity-100 transition-opacity" />
                                  {item.name}
                                </span>
                                <span className="text-sm font-black text-slate-900">{item.price}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  ))}
              </div>
            </div>
          );
        })}
      </div>

      {isAddingService && (
          <div className="fixed inset-0 z-[60] overflow-y-auto bg-slate-900/40 backdrop-blur-xl animate-in fade-in duration-300">
            <div className="flex min-h-full items-center justify-center p-6">
              <div className="bg-white rounded-[3rem] w-full max-w-2xl shadow-2xl overflow-hidden relative border border-white/20">
                <div className="p-8 border-b border-slate-50 flex justify-between items-center bg-white sticky top-0 z-10">
                  <div>
                    <h3 className="text-2xl font-black text-slate-900 tracking-tighter">
                      {editingServiceId ? texts.edit[language] : texts.create[language]}
                    </h3>
                    <p className="text-slate-400 text-sm font-bold">{texts.manage_pricing[language]}</p>
                  </div>
                  <button onClick={resetServiceForm} className="w-12 h-12 rounded-2xl bg-slate-50 flex items-center justify-center text-slate-400 hover:text-slate-900 transition-all"><X size={20} /></button>
                </div>
                
                <form onSubmit={handleSaveServiceSubmit} className="p-10 space-y-10">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    <div className="space-y-4">
                      <div className="space-y-2">
                        <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">
                          {texts.svc_name[language]} <span className="text-rose-500 ml-0.5">*</span>
                        </label>
                        <input 
                          type="text" 
                          value={newServiceName} 
                          onChange={(e) => setNewServiceName(e.target.value)} 
                          placeholder="e.g. Laptop Screen Repair"
                          className="w-full p-5 rounded-2xl bg-slate-50 font-black text-slate-900 outline-none focus:ring-4 focus:ring-violet-100 border-none transition-all placeholder:text-slate-300" 
                          required 
                        />
                      </div>

                      <div className="space-y-2">
                        <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">
                          {texts.cat[language]} <span className="text-rose-500 ml-0.5">*</span>
                        </label>
                        <select 
                          value={newServiceCategory} 
                          onChange={(e) => setNewServiceCategory(e.target.value)}
                          className="w-full p-5 rounded-2xl bg-slate-50 font-black text-slate-900 outline-none focus:ring-4 focus:ring-violet-100 border-none transition-all appearance-none cursor-pointer"
                        >
                          {SERVICE_CATEGORIES.map(cat => <option key={cat} value={cat}>{cat}</option>)}
                        </select>
                      </div>

                      <div className="space-y-2">
                        <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">{texts.visibility[language]}</label>
                        <div className="flex gap-2">
                           {(['PUBLIC', 'PRIVATE'] as const).map(status => (
                             <button
                               key={status}
                               type="button"
                               onClick={() => setNewServiceStatus(status)}
                               className={`flex-1 py-3 rounded-xl text-[10px] font-black uppercase tracking-widest border-2 transition-all ${
                                 newServiceStatus === status ? 'bg-slate-900 border-slate-900 text-white shadow-lg' : 'bg-white border-slate-100 text-slate-400 hover:border-slate-200'
                               }`}
                             >
                               {status}
                             </button>
                           ))}
                        </div>
                      </div>
                    </div>

                    <div className="space-y-4">
                      <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1 flex justify-between items-center">
                        <span>{texts.cover[language]}</span>
                        <button
                          type="button"
                          onClick={handleGenerateServiceImage}
                          disabled={isGeneratingImage || !newServiceName}
                          className={`flex items-center text-[10px] font-black transition-colors ${isGeneratingImage || !newServiceName ? 'text-slate-200 cursor-not-allowed' : 'text-violet-600 hover:text-violet-700'}`}
                        >
                          {isGeneratingImage ? <Loader2 size={12} className="mr-2 animate-spin"/> : <Sparkles size={12} className="mr-2" fill="currentColor"/>}
                          {texts.ai_gen[language]}
                        </button>
                      </label>
                      <div className="relative h-44 rounded-3xl overflow-hidden bg-slate-50 border-2 border-dashed border-slate-200 flex flex-col items-center justify-center group cursor-pointer">
                        {newServiceImage ? (
                           <img src={newServiceImage} className="w-full h-full object-cover" />
                        ) : (
                           <div className="text-center p-6">
                              <ImageIcon size={32} className="mx-auto text-slate-200 mb-2" />
                              <p className="text-[9px] font-black text-slate-300 uppercase tracking-widest">{texts.upload_hint[language]}</p>
                           </div>
                        )}
                        <input type="file" className="absolute inset-0 opacity-0 cursor-pointer" accept="image/*" onChange={(e) => {
                          const file = e.target.files?.[0];
                          if(file) {
                             const r = new FileReader();
                             r.onload = () => setNewServiceImage(r.result as string);
                             r.readAsDataURL(file);
                          }
                        }} />
                      </div>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">{texts.desc[language]}</label>
                    <textarea 
                      value={newServiceDescription} 
                      onChange={(e) => setNewServiceDescription(e.target.value)}
                      placeholder="..."
                      className="w-full p-5 rounded-2xl bg-slate-50 font-medium text-slate-900 outline-none focus:ring-4 focus:ring-violet-100 border-none transition-all h-24 resize-none"
                    />
                  </div>

                  <div className="bg-slate-900 rounded-[2.5rem] p-8 text-white relative overflow-hidden">
                    <div className="absolute top-0 right-0 p-8 opacity-10">
                       <ListPlus size={48} />
                    </div>
                    <h4 className="text-sm font-black uppercase tracking-[0.2em] mb-6 flex items-center">
                      <Package size={18} className="mr-3 text-violet-400" /> {texts.line_items[language]} <span className="text-rose-500 ml-1">*</span>
                    </h4>
                    
                    <div className="space-y-3 mb-6">
                      {tempServiceItems.map(item => (
                        <div key={item.id} className="flex items-center justify-between bg-white/5 border border-white/10 p-4 rounded-2xl backdrop-blur-sm group/line">
                          <div className="flex items-center">
                             <div className="w-2 h-2 rounded-full bg-violet-400 mr-4"></div>
                             <span className="text-sm font-bold">{item.name}</span>
                          </div>
                          <div className="flex items-center gap-4">
                            <span className="text-sm font-black text-violet-400">{item.price}</span>
                            <button type="button" onClick={() => handleRemoveTempItem(item.id)} className="text-white/20 hover:text-red-400 transition-colors"><Minus size={18} /></button>
                          </div>
                        </div>
                      ))}
                    </div>

                    <div className="flex flex-col md:flex-row gap-3">
                      <input 
                        type="text" 
                        value={currentItemName} 
                        onChange={(e) => setCurrentItemName(e.target.value)} 
                        className="flex-1 p-4 rounded-xl bg-white/10 border border-white/20 text-sm font-bold focus:bg-white/20 outline-none placeholder:text-white/20" 
                        placeholder={texts.item_placeholder[language]} 
                      />
                      <div className="flex gap-3">
                        <input 
                          type="number" 
                          value={currentItemPrice} 
                          onChange={(e) => setCurrentItemPrice(e.target.value)} 
                          className="w-32 p-4 rounded-xl bg-white/10 border border-white/20 text-sm font-bold focus:bg-white/20 outline-none placeholder:text-white/20" 
                          placeholder={`៛ ${texts.amount_placeholder[language]}`} 
                        />
                        <button 
                          type="button" 
                          onClick={handleAddItemToService} 
                          className="w-14 h-14 bg-violet-600 text-white rounded-xl flex items-center justify-center hover:bg-violet-500 transition-all shadow-lg"
                        >
                          <Plus size={24} />
                        </button>
                      </div>
                    </div>
                  </div>

                  <div className="pt-4">
                    <button 
                      type="submit" 
                      className="w-full py-6 bg-slate-900 text-white rounded-[2rem] font-black text-lg hover:bg-violet-600 transition-all disabled:opacity-30 disabled:cursor-not-allowed shadow-2xl shadow-slate-900/20 uppercase tracking-widest" 
                      disabled={!newServiceName || tempServiceItems.length === 0}
                    >
                      {editingServiceId ? texts.update[language] : texts.publish[language]}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
      )}
    </div>
  );
};